import {useEffect, useState} from 'react';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {copyAiChatImageToClipboard} from '@Components/ai-chat-window/lib/ai-chat-copy-image.library';
import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {isImageClipboardSupported} from '@Utils/clipboard.util';
import type {UseCopyAiChatImage, UseCopyAiChatImageParams} from './useCopyAiChatImage.types';
import {COPY_FEEDBACK_DURATION_MS, COPY_IMAGE_FAILED_GROWL_INTERVAL_MS, COPY_IMAGE_FAILED_GROWL_KEY, CopyAiChatImageStatus} from './useCopyAiChatImage.types';

export function useCopyAiChatImage({hashedId, source}: UseCopyAiChatImageParams): UseCopyAiChatImage {
  const [copyStatus, setCopyStatus] = useState(CopyAiChatImageStatus.IDLE);

  useEffect(() => {
    if (!isShowingCopyOutcome(copyStatus)) {
      return undefined;
    }

    const resetOutcomeTimeout = setTimeout((): void => {
      setCopyStatus(CopyAiChatImageStatus.IDLE);
    }, COPY_FEEDBACK_DURATION_MS);

    return (): void => {
      clearTimeout(resetOutcomeTimeout);
    };
  }, [copyStatus]);

  const onCopySucceeded = (): void => {
    setCopyStatus(CopyAiChatImageStatus.COPIED);
  };

  const onCopyFailed = (): void => {
    setCopyStatus(CopyAiChatImageStatus.FAILED);
    showCopyImageFailedGrowl();
  };

  const copyImage = (): void => {
    if (copyStatus === CopyAiChatImageStatus.COPYING) {
      return;
    }

    trackGA4CustomEvent(GA4EventName.AICHAT_COPY_IMAGE, {[GA4EventParamName.SOURCE]: source});
    setCopyStatus(CopyAiChatImageStatus.COPYING);
    void copyAiChatImageToClipboard(hashedId).then(onCopySucceeded).catch(onCopyFailed);
  };

  return {
    canCopyImage: isImageClipboardSupported(),
    copyStatus,
    copyImage,
  };
}

function isShowingCopyOutcome(copyStatus: CopyAiChatImageStatus): boolean {
  return copyStatus === CopyAiChatImageStatus.COPIED || copyStatus === CopyAiChatImageStatus.FAILED;
}

function showCopyImageFailedGrowl(): void {
  showMessageGrowl({
    text: window.i18next.t('pmwjs_copy_image_failed'),
    key: COPY_IMAGE_FAILED_GROWL_KEY,
    interval: COPY_IMAGE_FAILED_GROWL_INTERVAL_MS,
    showCloseIcon: true,
    type: GROWL_TYPE.DANGER,
  });
}
